/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, September 2020
 */

import React, { useCallback } from 'react';
import { border, color, layout, space } from 'styled-system';
import { Paragraph, styled, Theme } from '@staffino/primitive-controls';

const CheckboxInput = styled.input({
  height: 0,
  width: 0,
  opacity: 0,
  position: 'absolute',
  left: '-999rem',
});

type StyledPanelProps = {
  colorStyle?: string;
  hoverBorderColor?: string;
  theme?: Theme;
};

const StyledPanel = styled(Paragraph)<StyledPanelProps>(
  (props: StyledPanelProps) => ({
    paddingLeft: '2.1875rem',
    fontSize: '1rem',
    minHeight: '1.5625rem',
    lineHeight: '1.5625rem',

    '&::before': {
      content: "''",
      position: 'absolute' as const,
      width: 0,
      height: 0,
      top: '0.8125rem',
      left: '0.8125rem',
      borderRadius: '0.25rem',
      backgroundColor: props.colorStyle || props.theme.colors.primaryBackground,
      transition: 'all 200ms cubic-bezier(0.175, 0.885, 0.320, 1.275)',
    },

    'input:checked + &::before': {
      top: 0,
      left: 0,
      width: '1.625rem',
      height: '1.625rem',
    },

    'input:checked ~ &, &:hover, label:hover ~ &': {
      cursor: 'pointer',
    },

    'input:hover + &::after': {
      borderColor: props.hoverBorderColor,
      cursor: 'pointer',
    },

    '&::after': {
      content: "''",
      position: 'absolute' as const,
      width: '1.375rem',
      height: '1.375rem',
      top: 0,
      left: 0,
      border: `2px solid ${props.theme.colors.separator}`,
      borderRadius: '0.25rem',
    },
    'input:checked + &::after': {
      background: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='white' d='M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: '1rem',
      width: '1.625rem',
      height: '1.625rem',
      color: props.colorStyle || props.theme.colors.primaryText,
      border: 'none',
      textAlign: 'center' as const,
    },
  }),
  space,
  color,
  layout,
  border,
);
StyledPanel.defaultProps = {
  borderColor: 'separator',
  hoverBorderColor: '#a3b2bf',
};

export default function ChoiceCheckbox({
  innerRef,
  name,
  dataId = undefined,
  checked,
  disabled,
  colorStyle,
  onChange,
  children = undefined,
  ...rest
}) {
  const handleChange = useCallback(
    (event) => onChange(event.target.checked, event.target.dataset.id),
    [onChange],
  );
  return (
    <>
      <CheckboxInput
        ref={innerRef}
        type="checkbox"
        name={name}
        data-id={dataId}
        defaultValue={dataId}
        id={`${name}.${dataId}`}
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        {...rest}
      />
      <StyledPanel display="flex" colorStyle={colorStyle}>
        {children}
      </StyledPanel>
    </>
  );
}
